import React, { useEffect, useState, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import WizardStep from '../WizardStep';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { useReauth } from '@/contexts/ReauthContext';
import { getFlowIntent } from '@/lib/flowIntent';

const FUNCTIONS_BASE = 'https://diywpnlgowjzavjcpehw.supabase.co/functions/v1';

interface DownloadProductsStepProps {
  /** The feed to download, threaded explicitly from SelectFeedStep via the wizard. */
  feedId: string | null;
  /** Called once the sync completes; receives the product count for the selected feed. */
  onDone: (productCount: number) => void;
}

type DownloadStatus = 'starting' | 'fetching' | 'completed' | 'failed';

const DownloadProductsStep: React.FC<DownloadProductsStepProps> = ({ feedId, onDone }) => {
  const { t } = useTranslation('wizard');
  const { triggerReauth } = useReauth();

  const [status, setStatus] = useState<DownloadStatus>('starting');
  const [count, setCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const jobIdRef = useRef<string | null>(null);
  const doneRef = useRef(false);
  const doneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A1-NEU-1: `onDone` ist im Wizard eine Inline-Funktion, also bei jedem Render
  // der Elternkomponente eine neue Identitaet. Stuende sie in den Abhaengigkeiten
  // des Poll-Effekts, raeumte jeder Render das Sekunden-Intervall ab; bei
  // schnellen Renders feuerte der Poll nie. Der Ref traegt die aktuelle Fassung.
  const onDoneRef = useRef(onDone);
  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  // Clear the pending advance timer ONLY on unmount. It must NOT be cleared in the
  // poll effect's cleanup: the status→'completed' transition re-runs that effect, and
  // clearing there would cancel the very onDone timer we just scheduled — stranding the
  // wizard on the Download step (Optimize unreachable).
  useEffect(() => () => {
    if (doneTimerRef.current) clearTimeout(doneTimerRef.current);
  }, []);

  const start = useCallback(async () => {
    // No feed → surface a failure (never an endless spinner). This also guards the
    // back-navigation / direct-mount edge cases deterministically.
    if (!feedId) {
      setStatus('failed');
      setError(t('download.failed'));
      return;
    }
    setStatus('starting');
    setError(null);
    setCount(0);
    doneRef.current = false;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${FUNCTIONS_BASE}/enqueue-product-sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'apikey': session.access_token,
        },
        // notify_feed_check landet am Sync-Job: bei Completion enqueued der
        // DB-Trigger trg_feed_check_score den Score-Job server-seitig — die
        // Report-Mail kommt damit auch, wenn der Tab vorher geschlossen wird.
        body: JSON.stringify({ feedId, notify_feed_check: getFlowIntent() === 'feedcheck' }),
      });

      if (!response.ok) {
        let errorData: Record<string, unknown> = {};
        try { errorData = await response.json(); } catch { /* ignore parse errors */ }
        if (errorData.code === 'GOOGLE_REAUTH_REQUIRED') {
          triggerReauth(errorData as { googleAccountId: string; googleAccountEmail?: string }, () => start());
          return;
        }
        throw new Error((errorData.message as string) || (errorData.error as string) || `Enqueue failed (${response.status})`);
      }

      const data = await response.json();
      jobIdRef.current = data.job_id;
      setStatus('fetching');
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: 'wizard', action: 'download-enqueue' } });
      setError(err instanceof Error ? err.message : 'Unknown error');
      setStatus('failed');
    }
  }, [feedId, triggerReauth, t]);

  useEffect(() => { start(); }, [start]);

  useEffect(() => {
    if (status !== 'fetching' || !jobIdRef.current) return;
    const interval = setInterval(async () => {
      const { data: job } = await supabase
        .from('product_sync_jobs')
        .select('status, total_fetched')
        .eq('id', jobIdRef.current)
        .maybeSingle();
      if (!job) return;

      if (job.status === 'processing') {
        setCount(job.total_fetched || 0);
      } else if (job.status === 'completed') {
        clearInterval(interval);
        const { count: productCount } = await supabase
          .from('products')
          .select('*', { count: 'exact', head: true })
          .eq('feed_id', feedId);
        const finalCount = productCount || 0;
        setCount(finalCount);
        setStatus('completed');
        if (!doneRef.current) {
          doneRef.current = true;
          doneTimerRef.current = setTimeout(() => onDoneRef.current(finalCount), 800);
        }
      } else if (job.status === 'failed' || job.status === 'cancelled') {
        clearInterval(interval);
        setError(job.status === 'failed' ? t('download.failed') : t('download.cancelled'));
        setStatus('failed');
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [status, feedId, t]);

  return (
    <WizardStep title={t('download.title')} description={t('download.description')} hideNext hideBack>
      <div className="space-y-4 flex flex-col items-center py-2">
        {status === 'completed' ? (
          <CheckCircle2 className="w-12 h-12 text-primary" />
        ) : status === 'failed' ? (
          <AlertTriangle className="w-12 h-12 text-destructive" />
        ) : (
          <Spinner size="xl" />
        )}

        <p className="text-sm font-medium text-center">
          {status === 'failed'
            ? error
            : status === 'completed'
              ? t('download.done', { count })
              : t('download.progress', { count })}
        </p>

        {status === 'completed' && (
          <Progress value={100} variant="gradient" className="h-2.5 w-full" />
        )}

        {status === 'failed' && (
          <Button variant="outline" size="sm" onClick={() => start()}>
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            {t('download.retry')}
          </Button>
        )}
      </div>
    </WizardStep>
  );
};

export default DownloadProductsStep;
